import styles from './TiktokMicrosStatsBar.module.css';
import ProgressBar from '../../../../../../ui/ProgressBar/ProgressBar';
import cn from 'classnames';
import { asMoney, formatNumberToKNotation, roundTo2Dp, showNotification } from 'helpers';
import { TiktokMicroInfluencerPostLiveStats, microwave } from '@round/api';
import { ReactComponent as ArrowIcon } from '../../../../../../assets/ArrowIcon.svg';
import React, { useContext } from 'react';
import { ProtectedByUserGroups } from 'SharedComponents/ProtectedByUserGroup/ProtectedByUserGroups';
import { InfluencerPlanContext } from '../../../contexts/InfluencerPlanContext';
import { MICROWAVE_MANAGEMENT_URL } from 'SharedComponents/NavBar/Routes';
import { getPostCountDisplayValue } from 'Modules/Advertising/InfluencerPlan/features/MicrosStatsBar/helpers';
import CountBadge from 'ui-new/Badges/CountBadge/CountBadge';
import { useLinkedProject } from '../../Creators/components/CreatorbaseProjectSelect/hooks/useLinkedProject';
import ShareWithCreatorbaseToggle from '../../InfluencerPlanGroupBar/ShareWithCreatorbaseToggle';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useMicroCampaigns } from '../../../../Microwave/MicroCreators/contexts/MicroCampaign';
import useCreatorbaseProjectNavigationLink from '../../Creators/useCreatorbaseNavigationLink';

type TiktokMicrosStatsBarProps = {
    campaign: microwave.Campaign | undefined;
    microsStats: TiktokMicroInfluencerPostLiveStats | null;
    toApprovePostCount: number | null;
    isExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    children?: React.ReactNode | undefined;
    onClickAddMicroPost: () => void;
};

const TiktokMicrosStatsBar = ({
    campaign,
    microsStats,
    toApprovePostCount,
    onExpandedChange,
    isExpanded,
    children,
    onClickAddMicroPost,
}: TiktokMicrosStatsBarProps) => {
    const { influencerPlan } = useContext(InfluencerPlanContext);
    const { linkedProject } = useLinkedProject(influencerPlan?.application_manager_project_id);
    const { creatorbaseProject } = useCreatorbaseProjectNavigationLink(influencerPlan?.application_manager_project_id);
    const hasAccessToCreatorbaseProject = !!linkedProject || !!creatorbaseProject;
    const { update: updateCampaign } = useMicroCampaigns();

    const { currencies } = useCurrencies();
    const campaignCurrency = currencies.find((c) => c.id === campaign?.currency_id);

    const postCountDisplayValue = getPostCountDisplayValue(microsStats?.post_count ?? 0, campaign?.target_post_count);
    const budgetDisplayValue = asMoney(campaign?.budget, campaignCurrency);

    const amountSpentPercent =
        microsStats?.amount_spent && campaign?.budget ? (microsStats.amount_spent / campaign.budget) * 100 : 0;
    const amountSpentPercentDisplayValue = amountSpentPercent ? `${amountSpentPercent.toFixed(2)}%` : '-';

    const engagementRate = microsStats?.engagement_rate ? `${roundTo2Dp(microsStats?.engagement_rate * 100)}%` : '-';

    const totalViews =
        typeof microsStats?.total_views === 'number' ? formatNumberToKNotation(microsStats.total_views) : '-';

    return (
        <>
            <section className={styles.container}>
                <button
                    className={cn(styles.expandButton, { [styles.expanded]: isExpanded })}
                    onClick={() => onExpandedChange(!isExpanded)}
                >
                    <ArrowIcon />
                </button>
                <div className={styles.statsContainer}>
                    <div className={cn(styles.statColumn, styles.name)}>
                        <div className={styles.statHeading}>{campaign?.name ?? 'Micros'}</div>
                        <div className={styles.statValue}>{postCountDisplayValue}</div>
                    </div>

                    <div className={cn(styles.statColumn, styles.costStats)}>
                        <div className={styles.budgetRow}>
                            <div className={cn(styles.statHeading, styles.budget)}>{budgetDisplayValue}</div>
                            <div className={styles.statValue}>{amountSpentPercentDisplayValue}</div>
                        </div>

                        <ProgressBar
                            trackStyles={{
                                width: '100%',
                                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                                height: '0.33rem',
                                marginTop: '0.33rem',
                            }}
                            barStyles={{
                                backgroundColor: 'white',
                            }}
                            progress={amountSpentPercent}
                        />
                    </div>

                    <div className={cn(styles.statColumn, styles.views)}>
                        <div className={styles.statHeading}>Total Views</div>
                        <div className={styles.statValue}>{totalViews}</div>
                    </div>

                    <div className={cn(styles.statColumn, styles.engagementRate)}>
                        <div className={styles.statHeading}>Engagement Rate</div>
                        <div className={styles.statValue}>{engagementRate}</div>
                    </div>

                    {hasAccessToCreatorbaseProject && campaign && (
                        <ProtectedByUserGroups groups={['user_admin']}>
                            <ShareWithCreatorbaseToggle
                                isShared={campaign.is_shared_with_creatorbase}
                                onChange={async (isShared) => {
                                    if (!campaign) {
                                        return;
                                    }

                                    try {
                                        const response = await updateCampaign(campaign.id, {
                                            is_shared_with_creatorbase: isShared,
                                        });
                                        if (response.status === 200) {
                                            showNotification('Success', 'info');
                                            return;
                                        }

                                        if (response.status === 400) {
                                            showNotification(
                                                Array.isArray(response.data)
                                                    ? String(response.data)
                                                    : String(response.data.is_shared_with_creatorbase),
                                                'error'
                                            );
                                            return;
                                        }

                                        showNotification(response.data.detail, 'error');
                                    } catch {
                                        showNotification('Could not share campaign', 'error');
                                    }
                                }}
                            />
                        </ProtectedByUserGroups>
                    )}
                </div>
                <div className={styles.actionsContainer}>
                    <ProtectedByUserGroups groups={['microwave_admin']}>
                        <button className={styles.actionsContainerButton} onClick={onClickAddMicroPost}>
                            Add Post
                        </button>
                    </ProtectedByUserGroups>
                    {influencerPlan ? (
                        <ProtectedByUserGroups groups={['microwave_admin', 'round_planner']} shouldMatch="any">
                            <a
                                className={styles.actionsContainerButton}
                                href={`${MICROWAVE_MANAGEMENT_URL}approvals`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <CountBadge
                                    count={toApprovePostCount ?? '-'}
                                    className={cn({ [styles.alert]: Number(toApprovePostCount) > 0 })}
                                />
                                <span>Posts to approve ↗</span>
                            </a>
                        </ProtectedByUserGroups>
                    ) : null}
                </div>
            </section>
            {isExpanded && children}
        </>
    );
};

export default TiktokMicrosStatsBar;
